/**
 * Mapping statique `thème tactique → ligne d'ouverture` pour le mode
 * `tactic_drill_in_context` (MVP2.6 Gap 2, V0).
 *
 * Objectif pédagogique : habituer Léon au passage middlegame → tactique
 * (cf. PRODUCT.md §4.2). Avant de présenter la position tactique, on
 * anime les 10 premiers coups d'une ouverture cohérente avec le thème,
 * pour forcer la bascule mémoire-d'ouverture → calcul-tactique. C'est
 * une cause probable du gap Lichess Puzzle 2144 vs Blitz 1949 : Léon
 * voit bien les tactiques en isolation, moins bien quand elles arrivent
 * "en sortie de plan d'ouverture".
 *
 * V0 (cette implémentation) : mapping statique en dur. ~6-8 thèmes
 * suffisent pour valider le concept. Les coups choisis sont des mainlines
 * grand public sourcées de Lichess Masters et MCO-15. Pas d'eval Stockfish
 * requise — ce sont des coups d'ouverture connus, pas des évaluations.
 *
 * V1 (MVP2.7+, hors scope) : matching dynamique depuis
 * `opening_repertoire` de Léon — récupère ses 10 premiers coups
 * effectivement joués sur une partie représentative du thème. Nécessite
 * une corrélation `tactic_theme ↔ family_ECO` qu'on n'a pas encore.
 *
 * Format des coups : SAN standard (chess.js compatible). Validés par le
 * test `inContextMapping.test.ts` qui rejoue chaque ligne avec chess.js
 * pour s'assurer qu'aucun coup illégal n'est mappé.
 */

export interface InContextLine {
  /** Nom lisible affiché à Léon avant le drill (« Italienne classique »). */
  openingName: string;
  /**
   * Suite de coups SAN à animer. 10 = 5 blanc + 5 noir = profondeur classique
   * d'une "sortie d'ouverture" (cf. PRODUCT.md §4.2 « après 10 coups
   * d'ouverture »). On garde 10 même si certaines lignes mainline divergent
   * un peu plus tôt — couper sec à 10 est plus pédagogique pour Léon que de
   * jouer une 11ème variation qui aurait pu être une autre.
   */
  moves: string[];
  /** Source bibliographique (pour traçabilité, affiché en footer si voulu). */
  source: string;
}

/**
 * Mapping `tacticTheme → ligne d'ouverture cohérente`.
 *
 * Choix éditoriaux V0 (peut évoluer après retour Léon) :
 *  - `deflection`     → Italienne classique : les déviations sur f7 sont
 *                       le pain quotidien de cette ouverture (cf. Greco).
 *  - `pin`            → Espagnole Berlin : clouages classiques c6/d7.
 *  - `fork`           → Sicilienne Najdorf : fourchettes de cavalier d4 → e6
 *                       sur transferts e4-e5-e6 typiques.
 *  - `skewer`         → Caro-Kann Classical : enfilades sur la diagonale
 *                       a8-h1 après ouverture du grand fou noir.
 *  - `double_attack`  → Gambit Dame accepté : double attaques classiques
 *                       sur c4/d5 + plan b4 dame côté.
 *  - `discovered_attack` → Anglaise (c4) → e4 retardé : configurations
 *                       de cavalier qui libèrent la diagonale.
 *  - `removing_defender` → Française Tarrasch : le défenseur clé est
 *                       souvent le cavalier f6, donc thématique cohérent.
 *  - `mate`           → Italienne — mat des Berger / Greco fréquent en
 *                       sortie de cette ouverture chez les enfants 11 ans.
 *
 * Si le thème du puzzle n'est pas mappé, `getInContextLine` renvoie
 * `null` et le drill bascule en mode standard sans intro (fallback safe).
 */
const MAPPING: Record<string, InContextLine> = {
  deflection: {
    openingName: 'Italienne classique',
    moves: ['e4', 'e5', 'Nf3', 'Nc6', 'Bc4', 'Bc5', 'c3', 'Nf6', 'd4', 'exd4'],
    source: 'MCO-15 · Italian Game, Giuoco Piano',
  },
  pin: {
    openingName: 'Espagnole, défense Berlin',
    moves: ['e4', 'e5', 'Nf3', 'Nc6', 'Bb5', 'Nf6', 'O-O', 'Nxe4', 'd4', 'Nd6'],
    source: 'MCO-15 · Ruy Lopez Berlin Defence',
  },
  fork: {
    openingName: 'Sicilienne Najdorf',
    moves: ['e4', 'c5', 'Nf3', 'd6', 'd4', 'cxd4', 'Nxd4', 'Nf6', 'Nc3', 'a6'],
    source: 'MCO-15 · Sicilian Najdorf',
  },
  skewer: {
    openingName: 'Caro-Kann classique',
    moves: ['e4', 'c6', 'd4', 'd5', 'Nc3', 'dxe4', 'Nxe4', 'Bf5', 'Ng3', 'Bg6'],
    source: 'MCO-15 · Caro-Kann Classical',
  },
  double_attack: {
    openingName: 'Gambit Dame accepté',
    moves: ['d4', 'd5', 'c4', 'dxc4', 'Nf3', 'Nf6', 'e3', 'e6', 'Bxc4', 'c5'],
    source: 'MCO-15 · Queen\'s Gambit Accepted',
  },
  discovered_attack: {
    openingName: 'Anglaise symétrique',
    moves: ['c4', 'c5', 'Nc3', 'Nc6', 'g3', 'g6', 'Bg2', 'Bg7', 'Nf3', 'Nf6'],
    source: 'MCO-15 · English Symmetrical',
  },
  removing_defender: {
    openingName: 'Française Tarrasch',
    moves: ['e4', 'e6', 'd4', 'd5', 'Nd2', 'Nf6', 'e5', 'Nfd7', 'Bd3', 'c5'],
    source: 'MCO-15 · French Tarrasch',
  },
  mate: {
    openingName: 'Italienne — schéma offensif',
    moves: ['e4', 'e5', 'Nf3', 'Nc6', 'Bc4', 'Bc5', 'O-O', 'Nf6', 'd3', 'd6'],
    source: 'MCO-15 · Italian Game, Modern Variation',
  },
};

/**
 * Renvoie la ligne d'ouverture mappée pour un thème tactique, ou `null` si
 * le thème n'est pas mappé. Le caller fallback en mode standard sans
 * intro animée si null.
 */
export function getInContextLine(tacticTheme: string): InContextLine | null {
  return MAPPING[tacticTheme] ?? null;
}

/** Exposé pour les tests + dashboard parent (« thèmes couverts in-context »). */
export function listMappedThemes(): string[] {
  return Object.keys(MAPPING);
}
